import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Cake, PartyPopper, Trophy, ExternalLink } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";

/* Celebratory hero — sits at the top of the dashboard.
   Priority (set by backend): birthday > anniversary > project_complete.
   Phase 154 — Founders can set a `celebration_media_url` (Google Drive
   slideshow/GIF/video share link) on a Team Member or Project record; when
   present it takes over the media panel via an <iframe> (Drive's own embed
   preview) or <img>/<video> for a direct file URL. Falls back to the
   original behavior — a Giphy "translate" GIF, or the static emoji if that
   fails — when no custom link is set. */
const GIPHY_KEY = "dc6zaTOxFJmzC"; // Giphy public dev key (free, rate-limited)

// Converts a Google Drive "share" link (file/d/<id>/view or open?id=<id>)
// into its embeddable preview URL. Non-Drive URLs pass through unchanged —
// treated as a direct image/video/GIF file instead (see mediaKind below).
function toEmbeddableUrl(url) {
  if (!url) return null;
  const m = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([\w-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  return url;
}
function isDirectMediaFile(url) {
  return /\.(gif|png|jpe?g|webp|mp4|webm|mov)(\?|$)/i.test(url || "");
}

const HERO_META = {
  birthday: {
    icon: Cake,
    chip: "🎂 Birthday",
    color: "from-pink-500 via-orange-500 to-amber-500",
    gifQuery: "happy birthday celebration confetti",
    headline: (h) => `Happy birthday, ${h.name?.split(" ")[0]}! 🎉`,
    sub: (h) => h.age ? `Turning ${h.age} today · ${h.title || "the team"}` : (h.title || "Cheers from the studio"),
  },
  anniversary: {
    icon: PartyPopper,
    chip: "🥂 Work anniversary",
    color: "from-emerald-500 via-teal-500 to-cyan-500",
    gifQuery: "anniversary celebration cheers",
    headline: (h) => `${h.years} ${h.years === 1 ? "year" : "years"} in, ${h.name?.split(" ")[0]}! 🥂`,
    sub: (h) => `${h.title || "Team Analogy"} · Joined ${h.years} ${h.years === 1 ? "year" : "years"} ago today`,
  },
  project_complete: {
    icon: Trophy,
    chip: "🏆 Project shipped",
    color: "from-violet-500 via-fuchsia-500 to-pink-500",
    gifQuery: "trophy success high five team",
    headline: (h) => `Shipped: ${h.name}`,
    sub: (h) => `${h.client_name || "Internal"} · Completed ${formatDate(h.completed_at, { fallback: "recently" })}`,
  },
};

export default function CelebrationHero({ hero, allCelebrations }) {
  const [gifUrl, setGifUrl] = useState(null);
  const meta = HERO_META[hero?.kind];

  useEffect(() => {
    if (!meta || hero?.celebration_media_url) return; // Phase 154 — custom media takes over; skip the Giphy fetch entirely.
    let alive = true;
    const url = `https://api.giphy.com/v1/gifs/translate?api_key=${GIPHY_KEY}&s=${encodeURIComponent(meta.gifQuery)}&weirdness=4`;
    fetch(url).then((r) => r.ok ? r.json() : null).then((j) => {
      const u = j?.data?.images?.fixed_height?.url || j?.data?.images?.original?.url;
      if (alive && u) setGifUrl(u);
    }).catch(() => {});
    return () => { alive = false; };
  }, [hero?.kind, hero?.user_id, hero?.project_id, hero?.celebration_media_url, meta]);

  if (!hero || !meta) return null;
  const Icon = meta.icon;
  const others = [
    ...(allCelebrations?.birthdays || []).filter((x) => x.user_id !== hero.user_id).slice(0, 3),
    ...(allCelebrations?.anniversaries || []).filter((x) => x.user_id !== hero.user_id).slice(0, 3),
  ];

  return (
    <div className={`mb-6 relative overflow-hidden rounded-sm border border-slate-200 bg-gradient-to-br ${meta.color} text-white shadow-lg`} data-testid={`celebration-hero-${hero.kind}`}>
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "20px 20px" }} />
      <div className="relative grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 p-5 sm:p-7">
        <div className="min-w-0 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] bg-white/15 backdrop-blur rounded-sm px-2 py-1 mb-3 font-bold">
              <Icon size={11} /> {meta.chip}
            </div>
            <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight" data-testid="hero-headline">
              {meta.headline(hero)}
            </div>
            <div className="text-sm text-white/85 mt-2">{meta.sub(hero)}</div>
          </div>
          {hero.kind === "project_complete" && hero.project_id && (
            <Link to={`/projects/${hero.project_id}`} className="inline-flex items-center gap-1.5 text-xs bg-white/15 hover:bg-white/25 backdrop-blur rounded-sm px-3 py-1.5 mt-3 w-fit transition-colors" data-testid="hero-cta">
              Open project <ExternalLink size={11} />
            </Link>
          )}
          {others.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-wider opacity-75 mb-1.5">Also celebrating today</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {others.map((o) => (
                  <span key={`${o.kind}:${o.user_id || o.project_id}`} className="text-[11px] bg-white/15 backdrop-blur rounded-sm px-2 py-0.5">
                    {o.name} {o.kind === "anniversary" ? `· ${o.years}y` : o.kind === "birthday" && o.age ? `· ${o.age}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Media panel — Phase 154: a Founder-set celebration_media_url wins
            (Google Drive embed preview, or a direct image/gif/video file);
            otherwise the original Giphy-then-emoji behavior is untouched. */}
        <div className="hidden md:block">
          <div className="aspect-video rounded-sm bg-white/10 backdrop-blur overflow-hidden flex items-center justify-center" data-testid="hero-gif">
            {hero.celebration_media_url ? (
              isDirectMediaFile(hero.celebration_media_url) ? (
                /\.(mp4|webm|mov)(\?|$)/i.test(hero.celebration_media_url) ? (
                  <video src={hero.celebration_media_url} className="w-full h-full object-cover" autoPlay loop muted playsInline data-testid="hero-custom-media" />
                ) : (
                  <img src={hero.celebration_media_url} alt="celebration" className="w-full h-full object-cover" data-testid="hero-custom-media" />
                )
              ) : (
                <iframe
                  src={toEmbeddableUrl(hero.celebration_media_url)}
                  className="w-full h-full border-0"
                  allow="autoplay"
                  title="Celebration media"
                  data-testid="hero-custom-media"
                />
              )
            ) : gifUrl ? (
              <img src={gifUrl} alt="celebration" className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="text-6xl">{hero.kind === "birthday" ? "🎂" : hero.kind === "anniversary" ? "🥂" : "🏆"}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
